import { useState, useEffect } from 'react';
import { DatabaseSetup } from '../../utils/database-setup';

interface SetupStatus {
  isReady: boolean;
  missing: string[];
  recommendations: string[];
}

/**
 * Development component to check database setup status
 * Only shown in development mode
 */
export function DatabaseStatus() {
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    checkDatabaseStatus();
  }, []);

  const checkDatabaseStatus = async () => {
    setLoading(true);
    try {
      const setupStatus = await DatabaseSetup.getSetupStatus();
      setStatus(setupStatus);
    } catch (error) {
      console.error('Error checking database status:', error);
      setStatus({
        isReady: false,
        missing: ['Database connection failed'],
        recommendations: ['Check your Supabase configuration']
      });
    } finally {
      setLoading(false);
    }
  };

  // Only show in development
  if (import.meta.env.PROD) {
    return null;
  }

  if (loading) {
    return (
      <div className="fixed bottom-4 right-4 bg-blue-100 border border-blue-300 rounded-lg p-3 shadow-lg">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm text-blue-800">Checking database...</span>
        </div>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 max-w-sm">
      <div className={`border rounded-lg p-3 shadow-lg ${
        status.isReady 
          ? 'bg-green-100 border-green-300' 
          : 'bg-red-100 border-red-300'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${
              status.isReady ? 'bg-green-500' : 'bg-red-500'
            }`}></div>
            <span className={`text-sm font-medium ${
              status.isReady ? 'text-green-800' : 'text-red-800'
            }`}>
              Database {status.isReady ? 'Ready' : 'Setup Required'}
            </span>
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className={`text-xs px-2 py-1 rounded ${
              status.isReady 
                ? 'bg-green-200 text-green-800 hover:bg-green-300' 
                : 'bg-red-200 text-red-800 hover:bg-red-300'
            }`}
          >
            {showDetails ? 'Hide' : 'Details'}
          </button>
        </div>

        {showDetails && !status.isReady && (
          <div className="mt-3 space-y-2">
            <div>
              <h4 className="text-xs font-semibold text-red-800 mb-1">Missing:</h4>
              <ul className="text-xs text-red-700 space-y-1">
                {status.missing.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-500 mr-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="text-xs font-semibold text-red-800 mb-1">Recommendations:</h4>
              <ul className="text-xs text-red-700 space-y-1">
                {status.recommendations.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-500 mr-1">→</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <button
              onClick={checkDatabaseStatus}
              className="w-full mt-2 text-xs bg-red-200 text-red-800 px-2 py-1 rounded hover:bg-red-300"
            >
              Recheck Status
            </button>
          </div>
        )}

        {showDetails && status.isReady && (
          <div className="mt-3">
            <p className="text-xs text-green-700">
              All database components are properly configured and accessible.
            </p>
            <button
              onClick={checkDatabaseStatus}
              className="w-full mt-2 text-xs bg-green-200 text-green-800 px-2 py-1 rounded hover:bg-green-300"
            >
              Recheck Status
            </button>
          </div>
        )}
      </div>
    </div>
  );
}